import React, { FC, useState } from 'react';

import { Box, Checkbox } from '@rbilabs/universal-components';
import { CardType, IPaymentErrors, IPaymentState } from 'features/payment/types';
import { useIntl } from 'react-intl';

import { TextInput } from 'components/ucl/text-input';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import useCreditCardFormProps from 'hooks/form/use-credit-card-form-props';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import {
  PaymentFieldVariations,
  defaultPaymentFieldVariation,
} from 'state/launchdarkly/variations';
import { primitive } from 'styles/constants/primitives';

import FirstDataCreditCardInputsRedesign from './first-data-credit-card-form-inputs/first-data-credit-card-form-inputs-redesign';
import {
  FormContainer,
  StyledBillingZipWrapper,
  StyledNameOnCardWrapper,
  StyledSaveCardContainer,
} from './styled';
import { getErrorMessage } from './utils';

interface ICreditCardFormInputs {
  errors: IPaymentErrors;
  isDelivery?: boolean;
  onChange: (name: string, value: string) => void;
  paymentValues: IPaymentState;
  showSaveCard?: boolean;
  withBorder?: boolean;
  withPadding?: boolean;
  ariaLabel?: string;
  supportedCardTypes?: CardType[];
  validateCreditCardForm?: () => boolean;
  isPrepaidReload?: boolean;
  isUpdate?: boolean;
  isQuickPayMethodSelected?: boolean;
}

const CreditCardFormInputs: FC<React.PropsWithChildren<ICreditCardFormInputs>> = ({
  errors,
  isDelivery = false,
  onChange,
  paymentValues,
  showSaveCard = true,
  withBorder = false,
  withPadding = true,
  ariaLabel,
  supportedCardTypes = [],
  validateCreditCardForm,
  isPrepaidReload = false,
  isUpdate = false,
  isQuickPayMethodSelected = false,
}) => {
  const paymentFieldVariations =
    useFlag<PaymentFieldVariations>(LaunchDarklyFlag.PAYMENT_FIELD_VARIATIONS) ||
    defaultPaymentFieldVariation;
  const { feCountryCode } = useLocale();
  // If the country field is hidden from the form, use the user's current locale
  const country = paymentFieldVariations.country ? paymentValues.billingCountry : feCountryCode;

  // retrieve additional credit card form props
  const { postalCodeInputProps } = useCreditCardFormProps(country ?? undefined);
  const { formatMessage } = useIntl();
  const errorMessage = getErrorMessage({ errors, formatMessage });

  const [isCardNameInputFocused, setIsCardNameInputFocused] = useState(false);
  const [isZipCodeInputFocused, setIsZipCodeInputFocused] = useState(false);

  // NativeBase is having a weird behavior with the Checkbox
  // The onChange handler passed to it is always stale, unless we force a re-render by changing its key
  const checkboxKey = React.useMemo(() => JSON.stringify(paymentValues), [paymentValues]);

  const [didBlur, setDidBlur] = useState({
    cardNumber: !!paymentValues.cardNumber,
    expiry: !!paymentValues.expiry,
    cvv: !!paymentValues.cvv,
    billingZip: !!paymentValues.billingZip,
  });

  const validateOnBlur = (fieldName: string) => {
    setDidBlur({ ...didBlur, [fieldName]: true } as any);
    validateCreditCardForm?.();
  };
  return (
    <>
      {!!errorMessage && <VisuallyHidden accessibilityLabel={errorMessage} role="alert" />}
      <FormContainer
        withPadding={withPadding}
        withBorder={withBorder}
        accessibilityLabel={ariaLabel}
        marginTop="$6"
        isPaymentRedesignEnabled={true}
      >
        <FirstDataCreditCardInputsRedesign
          didBlur={didBlur}
          validateOnBlur={validateOnBlur}
          onChange={onChange}
          supportedCardTypes={supportedCardTypes}
          paymentValues={paymentValues}
          validateCreditCardForm={validateCreditCardForm}
          errors={errors}
          paymentFieldVariations={paymentFieldVariations}
        />
        {paymentFieldVariations.name && (
          <StyledNameOnCardWrapper marginBottom="0">
            <TextInput
              borderColor={primitive.$blackOpacity30}
              accessibilityLabel={
                !errors.nameOnCard
                  ? formatMessage({ id: 'nameOnCard' })
                  : formatMessage({ id: 'pleaseProvideTheCardName' })
              }
              onFocus={() => setIsCardNameInputFocused(true)}
              onBlur={() => {
                setIsCardNameInputFocused(false);
              }}
              onChangeText={(value: string) => onChange('nameOnCard', value)}
              label={formatMessage({ id: 'nameOnCard' })}
              value={paymentValues.nameOnCard}
              placeholder={isCardNameInputFocused ? 'John Smith' : ''}
              placeholderTextColor={primitive.$blackOpacity30}
              required
              testID="nameOnCard"
              errorMessage={errors.nameOnCard}
              autoComplete="name"
            />
          </StyledNameOnCardWrapper>
        )}

        {isDelivery && (
          <Checkbox
            key={checkboxKey}
            onChange={value => onChange('billingAddressSameAsDelivery', value ? 'true' : 'false')}
            value={String(paymentValues.billingAddressSameAsDelivery)}
            isChecked={paymentValues.billingAddressSameAsDelivery}
            testID="billing-same-as-delivery"
          >
            {formatMessage({ id: 'billingAddressIsSameAsDeliveryAddress' })}
          </Checkbox>
        )}
        {paymentFieldVariations.zip && (
          <StyledBillingZipWrapper marginBottom="0">
            {/* Additional props are spread: label, maxLength, pattern */}
            <TextInput
              borderColor={primitive.$blackOpacity30}
              onChangeText={value => onChange('billingZip', value)}
              value={paymentValues.billingZip}
              required
              testID="billingZip"
              accessibilityLabel={
                !didBlur.billingZip && !errors.didAttemptSubmit
                  ? formatMessage({ id: 'zipCode' })
                  : formatMessage({ id: 'pleaseProvideTheZipCode' })
              }
              onFocus={() => setIsZipCodeInputFocused(true)}
              onBlur={() => {
                validateOnBlur('billingZip');
                setIsZipCodeInputFocused(false);
              }}
              {...(isZipCodeInputFocused
                ? {
                    placeholder: formatMessage({ id: 'zipCode' }),
                    placeholderTextColor: primitive.$blackOpacity30,
                  }
                : null)}
              errorMessage={
                !didBlur.billingZip && !errors.didAttemptSubmit ? '' : errors.billingZip
              }
              placeholderTextColor={primitive.$blackOpacity30}
              autoComplete="postal-code"
              maxLength={10}
              isDisabled={isDelivery && paymentValues.billingAddressSameAsDelivery}
              {...postalCodeInputProps}
            />
          </StyledBillingZipWrapper>
        )}
      </FormContainer>

      {showSaveCard && (
        <StyledSaveCardContainer paddingLeft="$1">
          {isPrepaidReload || isUpdate ? (
            <Box marginTop="$2">{formatMessage({ id: 'saveCardDisclaimerRedesign' })}</Box>
          ) : (
            <Checkbox
              testID="saveCard"
              key={checkboxKey}
              value={String(paymentValues.saveCard)}
              isChecked={paymentValues.saveCard}
              onChange={value => {
                onChange('saveCard', value ? 'true' : 'false');
              }}
              accessibilityLabel={formatMessage({ id: 'saveCardDisclaimer' })}
              _text={{ fontSize: 'sm' }}
              justifyContent="flex-start"
              marginTop="$2"
            >
              {formatMessage({ id: 'saveCardDisclaimerRedesign' })}
            </Checkbox>
          )}
        </StyledSaveCardContainer>
      )}
    </>
  );
};

export default CreditCardFormInputs;
